import { useState, useMemo } from 'react';
import { CreditCard, Landmark } from 'lucide-react';
import {
  usePublicPaymentLink,
  useSubmitPublicPayment,
  type AchPaySubmit,
  type CardPaySubmit,
  type PaymentConfirmation,
} from '@/hooks/use-receive-payments';
import { US_STATES } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { PhoneInput } from '@/components/ui/phone-input';
import { sanitizeDriversLicense, validatePhone } from '@/lib/validators';

type PaymentMethod = 'card' | 'ach';

// ── Shared form utilities ──

const inputBase = 'h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:ring-1';
const inputNormal = `${inputBase} border-input focus:border-primary focus:ring-primary`;
const inputError = `${inputBase} border-red-500 focus:border-red-500 focus:ring-red-500`;

type FieldErrors = Record<string, string>;

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1 text-xs text-red-500" role="alert">
      {message}
    </p>
  );
}

function fc(fieldErrors: FieldErrors, field: string): string {
  return fieldErrors[field] ? inputError : inputNormal;
}

function SectionHeader({ title }: { title: string }) {
  return <h3 className="text-sm font-semibold text-foreground border-b border-border pb-2">{title}</h3>;
}

// ── Form State Types ──

interface CardFormState {
  cardNumber: string;
  cardHolderName: string;
  expirationDate: string;
  cvv: string;
  cardZipCode: string;
  cardStreetAddress: string;
  email: string;
}

interface AchFormState {
  nameOnBankAccount: string;
  email: string;
  phone: string;
  routingNumber: string;
  accountNumber: string;
  accountType: string;
  accountCategory: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  dlState: string;
  dlNumber: string;
}

// ── Helpers ──

function formatCurrency(amount: string, currency: string) {
  return parseFloat(amount).toLocaleString('en-US', { style: 'currency', currency });
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

// ── Status / Success Pages ──

function StatusPage({ status, linkNumber }: { status: string; linkNumber: string }) {
  const messages: Record<string, { title: string; description: string; color: string }> = {
    paid: {
      title: 'Already Paid',
      description: 'This payment link has already been completed.',
      color: 'text-green-600',
    },
    expired: {
      title: 'Link Expired',
      description: 'This payment link has passed its due date and is no longer valid.',
      color: 'text-amber-600',
    },
    cancelled: {
      title: 'Link Cancelled',
      description: 'This payment link has been cancelled.',
      color: 'text-red-600',
    },
  };

  const info = messages[status] ?? {
    title: 'Unavailable',
    description: 'This payment link is not available.',
    color: 'text-gray-600',
  };

  return (
    <div className="text-center py-12">
      <p className={`text-xl font-semibold mb-2 ${info.color}`}>{info.title}</p>
      <p className="text-sm text-muted-foreground mb-1">{info.description}</p>
      <p className="text-xs text-muted-foreground">Reference: {linkNumber}</p>
    </div>
  );
}

function SuccessPage({ confirmation }: { confirmation: PaymentConfirmation }) {
  return (
    <div className="text-center py-10">
      <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
        <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Payment Submitted</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Your payment details have been received and are being processed.
      </p>
      <div className="border rounded-lg overflow-hidden text-left max-w-xs mx-auto">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
          <span className="text-sm text-muted-foreground">Reference</span>
          <span className="text-sm font-medium text-gray-900">{confirmation.referenceNumber}</span>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <span className="text-sm text-muted-foreground">Amount</span>
          <span className="text-sm font-semibold text-gray-900">
            {formatCurrency(confirmation.amount, confirmation.currency)}
          </span>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
          <span className="text-sm text-muted-foreground">Status</span>
          <span className="text-sm font-medium text-amber-600 capitalize">{confirmation.status}</span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-6">
        A confirmation email has been sent if an email address was provided.
      </p>
    </div>
  );
}

// ── Main Page ──

export function PayByLinkPage({ publicToken }: { publicToken: string }) {
  const { data, isLoading, isError } = usePublicPaymentLink(publicToken);
  const submitPayment = useSubmitPublicPayment(publicToken);

  const [method, setMethod] = useState<PaymentMethod>('card');
  const [confirmation, setConfirmation] = useState<PaymentConfirmation | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [achAuthorized, setAchAuthorized] = useState(false);
  const [achAuthTouched, setAchAuthTouched] = useState(false);

  const [card, setCard] = useState<CardFormState>({
    cardNumber: '',
    cardHolderName: '',
    expirationDate: '',
    cvv: '',
    cardZipCode: '',
    cardStreetAddress: '',
    email: '',
  });

  const [ach, setAch] = useState<AchFormState>({
    nameOnBankAccount: '',
    email: '',
    phone: '',
    routingNumber: '',
    accountNumber: '',
    accountType: 'business',
    accountCategory: 'checking',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    dlState: '',
    dlNumber: '',
  });

  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  const switchMethod = (m: PaymentMethod) => {
    if (method === m) return;
    setMethod(m);
    // Clear touched for fields of the other method
    const cardFields = ['cardNumber', 'cardHolderName', 'expirationDate', 'cvv', 'cardZipCode', 'cardStreetAddress'];
    const achFields = [
      'accountType',
      'accountCategory',
      'routingNumber',
      'accountNumber',
      'nameOnBankAccount',
      'email',
      'phone',
      'streetAddress',
      'city',
      'state',
      'zipCode',
      'dlState',
      'dlNumber',
    ];
    const fieldsToClear = m === 'ach' ? cardFields : achFields;
    setTouched((prev) => {
      const next = { ...prev };
      fieldsToClear.forEach((f) => delete next[f]);
      return next;
    });
  };

  // ── Validation ──

  const fieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {};

    if (method === 'card') {
      const rawCard = card.cardNumber.replace(/\s/g, '');
      if (!rawCard) {
        errors.cardNumber = 'Card number is required';
      } else if (rawCard.length < 12 || rawCard.length > 19) {
        errors.cardNumber = 'Card number must be between 12 and 19 digits';
      }

      if (card.cardHolderName.trim() && card.cardHolderName.trim().length < 2) {
        errors.cardHolderName = 'Card holder name must be at least 2 characters';
      }

      if (!card.expirationDate) {
        errors.expirationDate = 'Expiration date is required';
      } else if (!/^\d{2}\/\d{2}$/.test(card.expirationDate)) {
        errors.expirationDate = 'Expiration date must be in MM/YY format';
      } else {
        const [mm, yy] = card.expirationDate.split('/').map(Number);
        if (mm < 1 || mm > 12) {
          errors.expirationDate = 'Invalid month';
        } else {
          const now = new Date();
          const currentMonth = now.getMonth() + 1;
          const currentYear = now.getFullYear() % 100;
          if (yy < currentYear || (yy === currentYear && mm < currentMonth)) {
            errors.expirationDate = 'Card has expired';
          }
        }
      }

      if (!card.cvv) {
        errors.cvv = 'CVV is required';
      } else if (card.cvv.length < 3 || card.cvv.length > 4) {
        errors.cvv = 'CVV must be 3 or 4 digits';
      }

      if (!card.cardZipCode) {
        errors.cardZipCode = 'Zip code is required';
      } else if (card.cardZipCode.length !== 5) {
        errors.cardZipCode = 'Zip code must be 5 digits';
      }

      if (card.cardStreetAddress.length > 127) {
        errors.cardStreetAddress = 'Street address must not exceed 127 characters';
      }

      if (card.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(card.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    if (method === 'ach') {
      if (!ach.accountType) errors.accountType = 'Account is required';
      if (!ach.accountCategory) errors.accountCategory = 'Account type is required';

      if (!ach.routingNumber) {
        errors.routingNumber = 'Routing number is required';
      } else if (ach.routingNumber.length !== 9) {
        errors.routingNumber = 'Routing number must be 9 digits';
      }

      if (!ach.accountNumber) {
        errors.accountNumber = 'Account number is required';
      } else if (ach.accountNumber.length < 4) {
        errors.accountNumber = 'Account number must be at least 4 digits';
      } else if (ach.accountNumber.length > 20) {
        errors.accountNumber = 'Account number must not exceed 20 digits';
      }

      if (!ach.nameOnBankAccount.trim()) {
        errors.nameOnBankAccount = 'Name on bank account is required';
      } else if (ach.nameOnBankAccount.trim().length < 2) {
        errors.nameOnBankAccount = 'Name must be at least 2 characters';
      }

      if (ach.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ach.email)) {
        errors.email = 'Please enter a valid email address';
      }

      const phoneErr = validatePhone(ach.phone, true);
      if (phoneErr) errors.phone = phoneErr;

      if (ach.zipCode && ach.zipCode.length !== 5) {
        errors.zipCode = 'Zip code must be 5 digits';
      }

      if (!ach.state) {
        errors.state = 'State is required';
      }

      if (!ach.dlState) {
        errors.dlState = "Driver's license state is required";
      }

      if (!ach.dlNumber.trim()) {
        errors.dlNumber = "Driver's license number is required";
      }
    }

    return errors;
  }, [method, card, ach]);

  const isFormValid = Object.keys(fieldErrors).length === 0;

  // ── Loading / Error / Non-active states ──

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-sm text-muted-foreground">Loading payment details...</p>
      </div>
    );
  }

  if (isError || !data?.data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-xl shadow-sm border p-8 w-full max-w-md text-center">
          <p className="text-lg font-semibold text-red-600 mb-2">Link Not Found</p>
          <p className="text-sm text-muted-foreground">This payment link does not exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const link = data.data;

  if (confirmation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-10">
        <div className="bg-white rounded-xl shadow-sm border p-8 w-full max-w-md">
          <SuccessPage confirmation={confirmation} />
        </div>
      </div>
    );
  }

  if (link.status !== 'active') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-xl shadow-sm border p-8 w-full max-w-md">
          <StatusPage status={link.status} linkNumber={link.linkNumber} />
        </div>
      </div>
    );
  }

  // ── Submit ──

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    // Mark all relevant fields touched for validation display
    const allFields =
      method === 'card'
        ? ['cardNumber', 'cardHolderName', 'expirationDate', 'cvv', 'cardZipCode', 'cardStreetAddress', 'email']
        : [
            'accountType',
            'accountCategory',
            'routingNumber',
            'accountNumber',
            'nameOnBankAccount',
            'email',
            'phone',
            'streetAddress',
            'city',
            'state',
            'zipCode',
            'dlState',
            'dlNumber',
          ];
    const allTouched: Record<string, boolean> = {};
    allFields.forEach((f) => (allTouched[f] = true));
    setTouched(allTouched);

    if (method === 'ach') setAchAuthTouched(true);

    if (!isFormValid) return;
    if (method === 'ach' && !achAuthorized) return;

    let body: CardPaySubmit | AchPaySubmit;

    if (method === 'card') {
      body = {
        paymentMethod: 'card',
        cardNumber: card.cardNumber.replace(/\s/g, ''),
        cardHolderName: card.cardHolderName.trim(),
        expirationDate: card.expirationDate.trim(),
        cvv: card.cvv.trim(),
        cardZipCode: card.cardZipCode.trim(),
        cardStreetAddress: card.cardStreetAddress.trim() || undefined,
        email: card.email.trim() || undefined,
      };
    } else {
      const nameParts = ach.nameOnBankAccount.trim().split(/\s+/);
      const firstName = nameParts[0];
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      body = {
        paymentMethod: 'ach',
        firstName,
        lastName,
        email: ach.email.trim(),
        phone: ach.phone.trim(),
        routingNumber: ach.routingNumber.trim(),
        accountNumber: ach.accountNumber.trim(),
        // Form stores accountType=business/personal and accountCategory=checking/savings
        // but the API schema expects accountType=checking/savings and accountCategory=business/personal
        accountType: ach.accountCategory as 'checking' | 'savings',
        accountCategory: ach.accountType as 'business' | 'personal',
        streetAddress: ach.streetAddress.trim(),
        city: ach.city.trim(),
        state: ach.state.trim(),
        zipCode: ach.zipCode.trim(),
        dlState: ach.dlState.trim(),
        dlNumber: ach.dlNumber.trim(),
      };
    }

    try {
      const result = await submitPayment.mutateAsync(body);
      setConfirmation(result.data);
    } catch (err: any) {
      const msg = err?.error?.message || err?.message || 'Payment submission failed. Please try again.';
      setSubmitError(msg);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="mx-auto max-w-2xl space-y-5">
        {/* Payment Summary Header */}
        <div className="rounded-xl overflow-hidden shadow-sm border">
          <div className="bg-teal-700 px-6 py-5">
            <p className="text-white font-semibold text-lg">Complete Your Payment</p>
            {link.customerName && <p className="text-teal-100 text-sm mt-0.5">Hello, {link.customerName}</p>}
          </div>
          <div className="bg-white px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Amount Due</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(link.amount, link.currency)}</p>
            </div>
            {link.dueDate && (
              <div className="text-right">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Due Date</p>
                <p className="text-sm font-medium text-gray-700">{formatDate(link.dueDate)}</p>
              </div>
            )}
          </div>
          {link.notes && (
            <div className="px-6 py-3 border-t bg-gray-50">
              <p className="text-xs text-muted-foreground">{link.notes}</p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Payment Method */}
          <div className="rounded-lg border border-border bg-white p-5 space-y-4">
            <SectionHeader title="Payment Method" />
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => switchMethod('card')}
                className={cn(
                  'flex items-center justify-center gap-2 rounded-lg border-2 p-3 text-sm font-medium transition-all',
                  method === 'card'
                    ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                    : 'border-border text-muted-foreground hover:border-primary/40',
                )}
              >
                <CreditCard className="h-5 w-5" />
                Credit / Debit Card
              </button>
              <button
                type="button"
                onClick={() => switchMethod('ach')}
                className={cn(
                  'flex items-center justify-center gap-2 rounded-lg border-2 p-3 text-sm font-medium transition-all',
                  method === 'ach'
                    ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                    : 'border-border text-muted-foreground hover:border-primary/40',
                )}
              >
                <Landmark className="h-5 w-5" />
                Bank Account (ACH)
              </button>
            </div>
          </div>

          {/* Card Details */}
          {method === 'card' && (
            <div className="rounded-lg border border-border bg-white p-5 space-y-4">
              <SectionHeader title="Card Details" />

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  Card Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={card.cardNumber}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 19);
                      const formatted = digits.replace(/(.{4})/g, '$1 ').trim();
                      setCard((p) => ({ ...p, cardNumber: formatted }));
                    }}
                    onBlur={() => markTouched('cardNumber')}
                    placeholder="1234 5678 9012 3456"
                    maxLength={23}
                    inputMode="numeric"
                    className={`${fc(fieldErrors, 'cardNumber')} pl-9`}
                    aria-invalid={!!fieldErrors.cardNumber}
                  />
                </div>
                {touched.cardNumber && <FieldError message={fieldErrors.cardNumber} />}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Card Holder Name</label>
                <input
                  type="text"
                  value={card.cardHolderName}
                  onChange={(e) =>
                    setCard((p) => ({ ...p, cardHolderName: e.target.value.replace(/[^A-Za-z\s]/g, '') }))
                  }
                  onBlur={() => markTouched('cardHolderName')}
                  placeholder="John Doe"
                  className={fc(fieldErrors, 'cardHolderName')}
                  aria-invalid={!!fieldErrors.cardHolderName}
                />
                {touched.cardHolderName && <FieldError message={fieldErrors.cardHolderName} />}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Expiration Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={card.expirationDate}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9/]/g, '');
                      const slashCount = (value.match(/\//g) || []).length;
                      if (slashCount > 1) value = value.replace(/\/([^]*)$/, '$1');
                      const digits = value.replace(/\//g, '');
                      if (digits.length >= 2 && !value.includes('/')) {
                        value = digits.slice(0, 2) + '/' + digits.slice(2);
                      }
                      value = value.slice(0, 5);
                      setCard((p) => ({ ...p, expirationDate: value }));
                    }}
                    onBlur={() => markTouched('expirationDate')}
                    placeholder="MM/YY"
                    maxLength={5}
                    inputMode="numeric"
                    className={fc(fieldErrors, 'expirationDate')}
                    aria-invalid={!!fieldErrors.expirationDate}
                  />
                  {touched.expirationDate && <FieldError message={fieldErrors.expirationDate} />}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    CVV <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={card.cvv}
                    onChange={(e) => setCard((p) => ({ ...p, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                    onBlur={() => markTouched('cvv')}
                    placeholder="123"
                    maxLength={4}
                    inputMode="numeric"
                    className={fc(fieldErrors, 'cvv')}
                    aria-invalid={!!fieldErrors.cvv}
                  />
                  {touched.cvv && <FieldError message={fieldErrors.cvv} />}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Zip Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={card.cardZipCode}
                    onChange={(e) =>
                      setCard((p) => ({ ...p, cardZipCode: e.target.value.replace(/\D/g, '').slice(0, 5) }))
                    }
                    onBlur={() => markTouched('cardZipCode')}
                    placeholder="94102"
                    maxLength={5}
                    inputMode="numeric"
                    className={fc(fieldErrors, 'cardZipCode')}
                    aria-invalid={!!fieldErrors.cardZipCode}
                  />
                  {touched.cardZipCode && <FieldError message={fieldErrors.cardZipCode} />}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Street Address</label>
                <input
                  type="text"
                  value={card.cardStreetAddress}
                  onChange={(e) => setCard((p) => ({ ...p, cardStreetAddress: e.target.value.slice(0, 127) }))}
                  onBlur={() => markTouched('cardStreetAddress')}
                  placeholder="123 Main St, Suite 100"
                  maxLength={127}
                  className={fc(fieldErrors, 'cardStreetAddress')}
                  aria-invalid={!!fieldErrors.cardStreetAddress}
                />
                <div className="flex justify-between">
                  <div>{touched.cardStreetAddress && <FieldError message={fieldErrors.cardStreetAddress} />}</div>
                  <span className="mt-1 text-xs text-muted-foreground">{card.cardStreetAddress.length}/127</span>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Email (for receipt)</label>
                <input
                  type="email"
                  value={card.email}
                  onChange={(e) => setCard((p) => ({ ...p, email: e.target.value }))}
                  onBlur={() => markTouched('email')}
                  placeholder="you@example.com"
                  className={fc(fieldErrors, 'email')}
                  aria-invalid={!!fieldErrors.email}
                />
                {touched.email && <FieldError message={fieldErrors.email} />}
              </div>
            </div>
          )}

          {/* ACH — Account Information */}
          {method === 'ach' && (
            <div className="rounded-lg border border-border bg-white p-5 space-y-4">
              <SectionHeader title="Account Information" />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Account <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={ach.accountType}
                    onChange={(e) => setAch((p) => ({ ...p, accountType: e.target.value }))}
                    onBlur={() => markTouched('accountType')}
                    className={fc(fieldErrors, 'accountType')}
                  >
                    <option value="">Select</option>
                    <option value="business">Business</option>
                    <option value="personal">Personal</option>
                  </select>
                  {touched.accountType && <FieldError message={fieldErrors.accountType} />}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Account Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={ach.accountCategory}
                    onChange={(e) => setAch((p) => ({ ...p, accountCategory: e.target.value }))}
                    onBlur={() => markTouched('accountCategory')}
                    className={fc(fieldErrors, 'accountCategory')}
                  >
                    <option value="">Select</option>
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                  {touched.accountCategory && <FieldError message={fieldErrors.accountCategory} />}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Routing Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={ach.routingNumber}
                    onChange={(e) =>
                      setAch((p) => ({ ...p, routingNumber: e.target.value.replace(/\D/g, '').slice(0, 9) }))
                    }
                    onBlur={() => markTouched('routingNumber')}
                    placeholder="123456789"
                    maxLength={9}
                    inputMode="numeric"
                    className={fc(fieldErrors, 'routingNumber')}
                    aria-invalid={!!fieldErrors.routingNumber}
                  />
                  {touched.routingNumber && <FieldError message={fieldErrors.routingNumber} />}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Account Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={ach.accountNumber}
                    onChange={(e) =>
                      setAch((p) => ({ ...p, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 20) }))
                    }
                    onBlur={() => markTouched('accountNumber')}
                    placeholder="000123456789"
                    maxLength={20}
                    inputMode="numeric"
                    className={fc(fieldErrors, 'accountNumber')}
                    aria-invalid={!!fieldErrors.accountNumber}
                  />
                  {touched.accountNumber && <FieldError message={fieldErrors.accountNumber} />}
                </div>
              </div>
            </div>
          )}

          {/* ACH — Account Holder Information */}
          {method === 'ach' && (
            <div className="rounded-lg border border-border bg-white p-5 space-y-4">
              <SectionHeader title="Account Holder Information" />

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  Name on Bank Account <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={ach.nameOnBankAccount}
                  onChange={(e) => setAch((p) => ({ ...p, nameOnBankAccount: e.target.value }))}
                  onBlur={() => markTouched('nameOnBankAccount')}
                  placeholder="John Doe or Acme Corp"
                  className={fc(fieldErrors, 'nameOnBankAccount')}
                  aria-invalid={!!fieldErrors.nameOnBankAccount}
                />
                {touched.nameOnBankAccount && <FieldError message={fieldErrors.nameOnBankAccount} />}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">Email</label>
                  <input
                    type="email"
                    value={ach.email}
                    onChange={(e) => setAch((p) => ({ ...p, email: e.target.value }))}
                    onBlur={() => markTouched('email')}
                    placeholder="john@example.com"
                    className={fc(fieldErrors, 'email')}
                    aria-invalid={!!fieldErrors.email}
                  />
                  {touched.email && <FieldError message={fieldErrors.email} />}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <PhoneInput
                    value={ach.phone}
                    onValueChange={(digits) => setAch((p) => ({ ...p, phone: digits }))}
                    onBlur={() => markTouched('phone')}
                    placeholder="123-456-7890"
                    className={fc(fieldErrors, 'phone')}
                    aria-invalid={!!fieldErrors.phone}
                  />
                  {touched.phone && <FieldError message={fieldErrors.phone} />}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Street Address</label>
                <input
                  type="text"
                  value={ach.streetAddress}
                  onChange={(e) => setAch((p) => ({ ...p, streetAddress: e.target.value }))}
                  onBlur={() => markTouched('streetAddress')}
                  placeholder="123 Main St, Suite 100"
                  className={fc(fieldErrors, 'streetAddress')}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">City</label>
                  <input
                    type="text"
                    value={ach.city}
                    onChange={(e) => setAch((p) => ({ ...p, city: e.target.value.replace(/[^A-Za-z\s]/g, '') }))}
                    onBlur={() => markTouched('city')}
                    placeholder="San Francisco"
                    className={fc(fieldErrors, 'city')}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={ach.state}
                    onChange={(e) => setAch((p) => ({ ...p, state: e.target.value }))}
                    onBlur={() => markTouched('state')}
                    className={fc(fieldErrors, 'state')}
                  >
                    <option value="">Select State</option>
                    {US_STATES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  {touched.state && <FieldError message={fieldErrors.state} />}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">Zip Code</label>
                  <input
                    type="text"
                    value={ach.zipCode}
                    onChange={(e) => setAch((p) => ({ ...p, zipCode: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
                    onBlur={() => markTouched('zipCode')}
                    placeholder="94102"
                    maxLength={5}
                    inputMode="numeric"
                    className={fc(fieldErrors, 'zipCode')}
                    aria-invalid={!!fieldErrors.zipCode}
                  />
                  {touched.zipCode && <FieldError message={fieldErrors.zipCode} />}
                </div>
              </div>
            </div>
          )}

          {/* ACH — Account Holder ID */}
          {method === 'ach' && (
            <div className="rounded-lg border border-border bg-white p-5 space-y-4">
              <SectionHeader title="Account Holder ID" />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">ID Type</label>
                  <select value="drivers_license" disabled className={`${inputNormal} bg-muted cursor-not-allowed`}>
                    <option value="drivers_license">Driver&apos;s License</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Driver&apos;s License State <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={ach.dlState}
                    onChange={(e) => setAch((p) => ({ ...p, dlState: e.target.value }))}
                    onBlur={() => markTouched('dlState')}
                    className={fc(fieldErrors, 'dlState')}
                  >
                    <option value="">Select State</option>
                    {US_STATES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  {touched.dlState && <FieldError message={fieldErrors.dlState} />}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Driver&apos;s License Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={ach.dlNumber}
                    onChange={(e) => setAch((p) => ({ ...p, dlNumber: sanitizeDriversLicense(e.target.value) }))}
                    onBlur={() => markTouched('dlNumber')}
                    placeholder="D1234567"
                    className={fc(fieldErrors, 'dlNumber')}
                    aria-invalid={!!fieldErrors.dlNumber}
                  />
                  {touched.dlNumber && <FieldError message={fieldErrors.dlNumber} />}
                </div>
              </div>
            </div>
          )}

          {/* ACH — Authorization & Revocation Agreement */}
          {method === 'ach' && (
            <div className="rounded-lg border border-border bg-white p-5 space-y-4">
              <SectionHeader title="Authorization Agreement" />

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 leading-relaxed">
                <p>
                  I understand that this authorization will remain in effect until I cancel it by emailing to{' '}
                  {link.merchantContact}. I agree to notify by email of any changes to my account information or
                  termination of this agreement at least fifteen (15) days prior to the next billing date. If the above
                  noted payment dates fall on a weekend or holiday, I understand the payments may be executed on the
                  next business day. For ACH debits to my checking/savings account, I understand that because these are
                  electronic transactions, these funds may be withdrawn from my account as soon as the above noted
                  periodic transaction dates. In the case of an ACH Transaction being rejected for Non Sufficient Funds
                  (NSF) I understand that {link.merchantName} may at its discretion process the charge again within 5
                  days, and agree to pay an additional $25 charge for each attempt returned NSF which will be initiated
                  as a separate transaction from the authorized recurring payment. I acknowledge that the origination of
                  ACH transactions to my account must comply with the provisions of U.S. law. I certify that I am an
                  authorized user of this credit card/bank account and will not dispute these scheduled transactions
                  with my bank or credit card company, so long as the transactions correspond to the terms indicated in
                  this authorization form. I acknowledge that ACH transactions must comply with all applicable U.S. law
                  and NACHA Operating Rules.
                </p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={achAuthorized}
                  onChange={(e) => setAchAuthorized(e.target.checked)}
                  onBlur={() => setAchAuthTouched(true)}
                  className="mt-0.5 h-4 w-4 accent-primary flex-shrink-0"
                />
                <span className="text-sm text-foreground">
                  I have read and agree to the authorization and revocation terms above.{' '}
                  <span className="text-red-500">*</span>
                </span>
              </label>
              {achAuthTouched && !achAuthorized && (
                <p className="text-xs text-red-500" role="alert">
                  You must agree to the authorization terms to proceed.
                </p>
              )}
            </div>
          )}

          {/* Submit */}
          <div className="rounded-lg border border-border bg-white p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">Total Amount</span>
              <span className="text-2xl font-bold text-primary">{formatCurrency(link.amount, link.currency)}</span>
            </div>

            {submitError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{submitError}</p>
            )}

            <button
              type="submit"
              disabled={submitPayment.isPending}
              className="w-full h-11 rounded-lg bg-primary text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitPayment.isPending ? 'Processing...' : `Pay ${formatCurrency(link.amount, link.currency)}`}
            </button>

            <p className="text-center text-xs text-muted-foreground">
              Ref: {link.linkNumber} &nbsp;·&nbsp; Secured by Q PaymentZ
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
